// Copyright 2025-2026 - Hewlett Packard Enterprise Company
import {
  useCallback,
  useEffect,
  useState,
  useMemo,
  createContext,
} from 'react';

import useFlags from '../hooks/useFlags';

import { DARK_MODE_KEY } from '../constants/constants';
import { PRODUCT_CAPABILITIES } from '../constants/productCapabilities';

export const ThemeModeContext = createContext();

const THEME_MODE_PREFERENCES = {
  AUTO: 'auto',
  DARK: 'dark',
  LIGHT: 'light',
};
const VALID_PREFERENCES = Object.values(THEME_MODE_PREFERENCES);

const getModeString = isDarkMode => (isDarkMode ? 'dark' : 'light');
const isValidPreference = preference => VALID_PREFERENCES.includes(preference);

const getStoredPreference = () => {
  try {
    const storedPreference = localStorage.getItem(DARK_MODE_KEY);
    return isValidPreference(storedPreference)
      ? storedPreference
      : THEME_MODE_PREFERENCES.AUTO;
  } catch (_error) {
    return THEME_MODE_PREFERENCES.AUTO;
  }
};

const getSystemDarkMode = () => {
  if (
    typeof window === 'undefined' ||
    typeof window.matchMedia !== 'function'
  ) {
    return false;
  }

  return window.matchMedia('(prefers-color-scheme: dark)').matches;
};

const resolveIsDarkMode = ({
  useGlcpThemePreference,
  themeModePreference,
  isDarkModeSystemPreference,
}) => {
  if (useGlcpThemePreference) {
    if (themeModePreference === THEME_MODE_PREFERENCES.DARK) {
      return true;
    }

    if (themeModePreference === THEME_MODE_PREFERENCES.LIGHT) {
      return false;
    }
  }

  return isDarkModeSystemPreference;
};

const getThemeModePreference = () =>
  PRODUCT_CAPABILITIES.themePreferenceSupport
    ? getStoredPreference()
    : THEME_MODE_PREFERENCES.AUTO;

export default function ThemeModeProvider({ children }) {
  const flags = useFlags();
  const useGlcpThemePreference =
    flags.T_20260701_70791_USE_GLCP_THEME_PREFERENCE_BOOL();
  const isThemePreferenceSupported =
    PRODUCT_CAPABILITIES.themePreferenceSupport;
  const [themeModePreference, setThemeModePreferenceState] = useState(
    getThemeModePreference,
  );
  const [isDarkModeSystemPreference, setIsDarkModeSystemPreference] =
    useState(getSystemDarkMode);

  useEffect(() => {
    if (
      typeof window === 'undefined' ||
      typeof window.matchMedia !== 'function'
    ) {
      return undefined;
    }

    const darkModeMediaQuery = window.matchMedia(
      '(prefers-color-scheme: dark)',
    );

    const handleSystemThemeChange = event => {
      setIsDarkModeSystemPreference(event.matches);
    };

    setIsDarkModeSystemPreference(darkModeMediaQuery.matches);

    if (typeof darkModeMediaQuery.addEventListener === 'function') {
      darkModeMediaQuery.addEventListener('change', handleSystemThemeChange);

      return () => {
        darkModeMediaQuery.removeEventListener(
          'change',
          handleSystemThemeChange,
        );
      };
    }

    darkModeMediaQuery.addListener(handleSystemThemeChange);

    return () => {
      darkModeMediaQuery.removeListener(handleSystemThemeChange);
    };
  }, []);

  useEffect(() => {
    if (!isThemePreferenceSupported) {
      return undefined;
    }

    const handleStoredPreferenceChange = event => {
      if (event.key !== DARK_MODE_KEY) {
        return;
      }

      setThemeModePreferenceState(getStoredPreference());
    };

    window.addEventListener('storage', handleStoredPreferenceChange);

    return () => {
      window.removeEventListener('storage', handleStoredPreferenceChange);
    };
  }, [isThemePreferenceSupported]);

  const setThemeModePreference = useCallback(
    nextThemeModePreference => {
      if (
        !isThemePreferenceSupported ||
        !isValidPreference(nextThemeModePreference)
      ) {
        return;
      }

      try {
        localStorage.setItem(DARK_MODE_KEY, nextThemeModePreference);
      } catch (_error) {
        // ignore localStorage write errors and still update in-memory state
      }

      setThemeModePreferenceState(nextThemeModePreference);
    },
    [isThemePreferenceSupported],
  );

  const isDarkMode = resolveIsDarkMode({
    useGlcpThemePreference,
    themeModePreference,
    isDarkModeSystemPreference,
  });

  const value = useMemo(
    () => ({
      isDarkMode,
      themeMode: getModeString(isDarkMode),
      themeModePreference,
      setThemeModePreference,
    }),
    [isDarkMode, themeModePreference, setThemeModePreference],
  );

  return (
    <ThemeModeContext.Provider value={value}>
      {children}
    </ThemeModeContext.Provider>
  );
}
